import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { useAdminPlans } from "@/hooks/queries/useAdminPlans";
import {
  useSetUserSubscription,
  useCancelUserSubscription,
  useSetUserViews,
  useCreateTeamMember,
} from "@/hooks/mutations/useAdminUserMutations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { BackendError } from "@/utils/fetchToBackend";
import type { AdminUser } from "@/types/domain";

const EMPTY_MEMBER = { name: "", email: "", password: "" };

const DATE_INPUT_FORMAT = "YYYY-MM-DD";

/**
 * Gestión manual del acceso de un usuario: plan, vigencia y saldo de vistas.
 * Es el puente hasta que exista pricing self-serve.
 */
export function ManageUserDialog({
  user,
  onOpenChange,
}: {
  user: AdminUser | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: plans = [], isLoading: plansLoading } = useAdminPlans(!!user);
  const setSubscription = useSetUserSubscription();
  const cancelSubscription = useCancelUserSubscription();
  const setViews = useSetUserViews();
  const createTeamMember = useCreateTeamMember();

  const [planId, setPlanId] = useState<string>("");
  const [expiresat, setExpiresat] = useState("");
  const [views, setViewsValue] = useState("");
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [member, setMember] = useState(EMPTY_MEMBER);

  // Al abrir con otro usuario, resetear el formulario a su estado actual.
  useEffect(() => {
    if (!user) return;
    setPlanId(user.subscription ? String(user.subscription.planId) : "");
    setExpiresat(
      user.subscription?.expiresat
        ? dayjs(user.subscription.expiresat).format(DATE_INPUT_FORMAT)
        : dayjs().add(30, "day").format(DATE_INPUT_FORMAT),
    );
    setViewsValue(String(user.views.remaining));
    setShowTeamForm(false);
    setMember(EMPTY_MEMBER);
  }, [user]);

  // Sin fila de info extra (nunca envió el formulario) el backend no puede
  // guardar vistas: se deshabilita en lugar de fallar con 404.
  const canEditViews = !!user?.betaformcompleted;

  useEffect(() => {
    if (!planId && plans.length > 0) setPlanId(String(plans[0]!.plan_id));
  }, [plans, planId]);

  const selectedPlan = plans.find((p) => String(p.plan_id) === planId);
  const isSaving = setSubscription.isPending || setViews.isPending;

  function shiftExpiry(days: number) {
    setExpiresat(dayjs().add(days, "day").format(DATE_INPUT_FORMAT));
  }

  async function onSave() {
    if (!user) return;
    const parsedViews = Number(views);
    if (!planId || !dayjs(expiresat).isValid()) {
      toast.error("Selecciona un plan y una fecha de vencimiento válida.");
      return;
    }
    if (canEditViews && (!Number.isInteger(parsedViews) || parsedViews < 0)) {
      toast.error("Las vistas deben ser un número entero mayor o igual a 0.");
      return;
    }

    try {
      await setSubscription.mutateAsync({
        userId: user.id,
        planId: Number(planId),
        // Fin del día para que el vencimiento incluya la fecha elegida.
        expiresat: dayjs(expiresat).endOf("day").toISOString(),
      });
      if (canEditViews && parsedViews !== user.views.remaining) {
        await setViews.mutateAsync({ userId: user.id, views: parsedViews });
      }
      toast.success(`Acceso de ${user.name} actualizado.`);
      onOpenChange(false);
    } catch {
      toast.error("No pudimos actualizar el acceso. Intenta de nuevo.");
    }
  }

  async function onCreateTeamMember() {
    if (!user?.organizationId) return;
    if (
      !member.name.trim() ||
      !member.email.trim() ||
      member.password.length < 8
    ) {
      toast.error("Nombre, correo y una contraseña de 8 caracteres o más.");
      return;
    }

    try {
      await createTeamMember.mutateAsync({
        ...member,
        organizationId: user.organizationId,
      });
      toast.success(`${member.name} se sumó al equipo de ${user.name}.`);
      setMember(EMPTY_MEMBER);
      setShowTeamForm(false);
    } catch (err) {
      toast.error(
        (err as BackendError)?.error ?? "No pudimos crear al comercial.",
      );
    }
  }

  async function onCancelSubscription() {
    if (!user) return;
    try {
      await cancelSubscription.mutateAsync(user.id);
      toast.success(`Suscripción de ${user.name} cancelada.`);
      onOpenChange(false);
    } catch {
      toast.error("No pudimos cancelar la suscripción.");
    }
  }

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Gestionar acceso</DialogTitle>
          <DialogDescription>
            {user?.name} · {user?.email}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-1">
          <div className="flex flex-col gap-2">
            <Label htmlFor="plan">Plan</Label>
            <Select value={planId} onValueChange={setPlanId}>
              <SelectTrigger id="plan">
                <SelectValue
                  placeholder={plansLoading ? "Cargando…" : "Elige un plan"}
                />
              </SelectTrigger>
              <SelectContent>
                {plans.map((plan) => (
                  <SelectItem key={plan.plan_id} value={String(plan.plan_id)}>
                    {plan.plan_name}
                    {plan.plan_monthlyviews == null
                      ? " · vistas ilimitadas"
                      : plan.plan_viewsrenew
                        ? ` · ${plan.plan_monthlyviews} vistas/mes`
                        : ` · ${plan.plan_monthlyviews} vistas de por vida`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="expiresat">Vence el</Label>
            <Input
              id="expiresat"
              type="date"
              value={expiresat}
              onChange={(e) => setExpiresat(e.target.value)}
            />
            <div className="flex gap-2">
              {[7, 30, 90].map((days) => (
                <Button
                  key={days}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => shiftExpiry(days)}
                >
                  +{days} días
                </Button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="views">Vistas restantes este mes</Label>
            <Input
              id="views"
              type="number"
              min={0}
              value={views}
              disabled={!canEditViews}
              onChange={(e) => setViewsValue(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {canEditViews
                ? selectedPlan?.plan_monthlyviews == null
                  ? "El plan seleccionado es ilimitado: este saldo no se usa."
                  : `Al iniciar el próximo mes el saldo vuelve a ${selectedPlan.plan_monthlyviews} (límite del plan).`
                : "El usuario aún no envió el formulario de beta, no tiene saldo que ajustar."}
            </p>
          </div>
        </div>

        <Separator />

        <div className="flex flex-col gap-3">
          <div>
            <p className="text-sm font-medium">Equipo</p>
            <p className="text-xs text-muted-foreground">
              Suma un comercial a esta organización. Entra habilitado, sin
              organización propia y heredando el plan de la empresa.
            </p>
          </div>

          {!user?.organizationId ? (
            <p className="text-xs text-muted-foreground">
              Este usuario no tiene organización, no se le puede sumar gente.
            </p>
          ) : !showTeamForm ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              onClick={() => setShowTeamForm(true)}
            >
              <UserPlus className="h-4 w-4" />
              Agregar comercial
            </Button>
          ) : (
            <div className="flex flex-col gap-2">
              <Input
                placeholder="Nombre y apellido"
                value={member.name}
                onChange={(e) =>
                  setMember({ ...member, name: e.target.value })
                }
              />
              <Input
                type="email"
                placeholder="correo@empresa.com"
                value={member.email}
                onChange={(e) =>
                  setMember({ ...member, email: e.target.value })
                }
              />
              <Input
                type="text"
                placeholder="Contraseña (8+ caracteres)"
                value={member.password}
                onChange={(e) =>
                  setMember({ ...member, password: e.target.value })
                }
              />
              <p className="text-xs text-muted-foreground">
                Se la tienes que pasar tú: no se envía ningún correo. Entra con
                todas las líneas de negocio de la empresa asignadas — quien lleva
                la cuenta puede recortarlas después desde “Administrar equipo”.
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={createTeamMember.isPending}
                  onClick={onCreateTeamMember}
                >
                  {createTeamMember.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  Crear
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowTeamForm(false)}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>

        <Separator />

        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            disabled={!user?.subscription || cancelSubscription.isPending}
            onClick={onCancelSubscription}
          >
            {cancelSubscription.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            Cancelar suscripción
          </Button>
          <Button type="button" disabled={isSaving} onClick={onSave}>
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
